import React, { useState, useEffect, useRef } from 'react';
import { User } from '../types';
import ProfileSettingScreen, { ProfileSettingScreenRef } from './ProfileSettingScreen';

interface MenuScreenProps {
    currentUser: User;
    onLogout: () => void;
    onGoToPlaza: () => void;
    onGoToProfileSetting: () => void;
    onGoToDetailSettings?: () => void;
    onGoToSettings?: () => void;
    onGoToHelp: () => void;
    onGoToPrivacySecurity?: () => void;
    onGoToSecurityPolicy?: () => void;
    onGoToCommunityGuidelines?: () => void;
    onGoToTermsOfService?: () => void;
    onGoToAbout?: () => void;
    onUpdateUser?: (updatedUser: User) => void;
}

const MenuScreen: React.FC<MenuScreenProps> = ({
    currentUser,
    onLogout,
    onGoToPlaza,
    onGoToProfileSetting,
    onGoToDetailSettings,
    onGoToSettings,
    onGoToHelp,
    onGoToPrivacySecurity,
    onGoToSecurityPolicy,
    onGoToCommunityGuidelines,
    onGoToTermsOfService,
    onGoToAbout,
    onUpdateUser,
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const [news, setNews] = useState<any[]>([]);
    const [blogPosts, setBlogPosts] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [showProfileSettings, setShowProfileSettings] = useState(false);
    const profileSettingRef = useRef<ProfileSettingScreenRef>(null);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        const loadContent = async () => {
            try {
                setLoading(true);
                const API_URL = 'https://voicechatapi.kasaneko.com';

                const [newsResponse, blogResponse] = await Promise.all([
                    fetch(`${API_URL}/api/content/news?limit=10`),
                    fetch(`${API_URL}/api/content/blog?limit=10`),
                ]);

                const newsData = await newsResponse.json();
                const blogData = await blogResponse.json();

                if (newsData.success) {
                    setNews(newsData.news || []);
                }
                if (blogData.success) {
                    setBlogPosts(blogData.posts || []);
                }
            } catch (error) {
                console.error('コンテンツ取得エラー:', error);
            } finally {
                setLoading(false);
            }
        };

        loadContent();
    }, []);

    return (
        <div
            style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100dvh',
                background: 'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundRepeat: 'no-repeat',
                fontFamily: 'Arial, sans-serif',
                color: '#333',
                overflow: 'hidden',
            }}
        >
            {/* ヘッダー */}
            <div
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: isMobile ? '8%' : '10%',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                }}
            >
                {/* 左上: アプリアイコン */}
                <div
                    style={{
                        position: 'absolute',
                        left: isMobile ? '2%' : '3%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: isMobile ? '20px' : '28px',
                        fontWeight: 'bold',
                    }}
                >
                    🎙️ Voitter
                </div>

                {/* 右上: ログアウトボタン（PCのみ） */}
                {!isMobile && (
                    <button
                        onClick={onLogout}
                        style={{
                            position: 'absolute',
                            right: '3%',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: '#ff4757',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            width: '90px',
                            height: '36px',
                        }}
                    >
                        ログアウト
                    </button>
                )}
            </div>

            {/* メインコンテンツ（スクロール可能） */}
            <div
                style={{
                    position: 'absolute',
                    top: isMobile ? '8%' : '10%',
                    left: 0,
                    width: '100%',
                    height: isMobile ? '82%' : '87%',
                    overflow: isMobile ? 'auto' : 'hidden',
                }}
            >
                {/* コンテンツラッパー */}
                <div
                    style={{
                        width: '100%',
                        height: isMobile ? 'auto' : '100%',
                        paddingBottom: isMobile
                            ? 'calc(12% + env(safe-area-inset-bottom, 0px) + 20px)'
                            : '0px',
                        display: isMobile ? 'block' : 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                    }}
                >
                    {/* スマホ: 楽しくお話ししよう！要素 */}
                    {isMobile && (
                        <div
                            style={{
                                width: '100%',
                                padding: '40px 20px',
                                marginBottom: '20px',
                                textAlign: 'center',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '28px',
                                    fontWeight: 'bold',
                                    color: '#333',
                                    textShadow: 'none',
                                    lineHeight: '1.6',
                                }}
                            >
                                楽しくお話ししよう！✨
                            </div>
                        </div>
                    )}

                    {/* ブログおよびニュース（PCは3列、スマホは縦並び） */}
                    <div
                        style={{
                            width: isMobile ? '100%' : '100%',
                            height: isMobile ? 'auto' : '100%',
                            display: 'flex',
                            flexDirection: isMobile ? 'column' : 'row',
                            marginBottom: isMobile ? '20px' : '0',
                            gap: isMobile ? '15px' : '15px',
                            alignItems: isMobile ? 'stretch' : 'flex-start',
                            overflow: isMobile ? 'visible' : 'hidden',
                        }}
                    >
                        {/* お知らせ */}
                        <div
                            style={{
                                width: isMobile ? 'calc(100% - 30px)' : '30%',
                                marginLeft: isMobile ? '15px' : '0',
                                marginRight: isMobile ? '15px' : '0',
                                height: isMobile ? '250px' : '100%',
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '0',
                                boxSizing: 'border-box',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: isMobile ? '16px' : '18px',
                                    fontWeight: 'bold',
                                    borderBottom: '2px solid rgba(0, 0, 0, 0.2)',
                                    padding: '20px 20px 10px 20px',
                                    marginBottom: '0',
                                    position: 'sticky',
                                    top: 0,
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(10px)',
                                    zIndex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                }}
                            >
                                <a
                                    href='https://voicechatinfo.kasaneko.com/#news'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    style={{
                                        textDecoration: 'none',
                                        color: '#333',
                                        fontSize: isMobile ? '20px' : '22px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    title='お知らせページを開く'
                                >
                                    🔗
                                </a>
                                📢 お知らせ
                            </div>
                            <div
                                style={{
                                    width: '100%',
                                    flex: 1,
                                    overflow: 'auto',
                                    padding: '15px 20px 20px 20px',
                                }}
                            >
                                {loading ? (
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            color: '#666',
                                            fontSize: isMobile ? '12px' : '14px',
                                        }}
                                    >
                                        読み込み中...
                                    </div>
                                ) : news.length === 0 ? (
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            color: '#666',
                                            fontSize: isMobile ? '12px' : '14px',
                                        }}
                                    >
                                        お知らせはありません
                                    </div>
                                ) : (
                                    news.map((item, index) => (
                                        <div
                                            key={item.newsId || index}
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                border: '1px solid rgba(0, 0, 0, 0.15)',
                                                borderRadius: '12px',
                                                marginBottom: '10px',
                                                fontSize: isMobile ? '11px' : '13px',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: isMobile ? '10px' : '12px',
                                                    color: '#666',
                                                }}
                                            >
                                                {item.createdAt
                                                    ? new Date(item.createdAt).toLocaleDateString(
                                                          'ja-JP',
                                                          {
                                                              year: 'numeric',
                                                              month: 'long',
                                                              day: 'numeric',
                                                          }
                                                      )
                                                    : ''}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: isMobile ? '12px' : '14px',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {item.title}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: isMobile ? '11px' : '13px',
                                                    lineHeight: '1.6',
                                                    color: '#333',
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {item.content}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>

                        {/* PC: 真ん中の要素エリア */}
                        {!isMobile && (
                            <div
                                style={{
                                    width: '40%',
                                    height: '85%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    gap: '20px',
                                    alignSelf: 'flex-start',
                                    overflow: 'auto',
                                }}
                            >
                                {/* 1. 楽しくお話ししよう！要素 */}
                                <div
                                    style={{
                                        width: '100%',
                                        padding: '70px 20px',
                                        textAlign: 'center',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '12px',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: '32px',
                                            fontWeight: 'bold',
                                            color: '#333',
                                            textShadow: 'none',
                                            lineHeight: '1.6',
                                        }}
                                    >
                                        楽しくお話ししよう！✨
                                    </div>
                                </div>

                                {/* 2. 設定、通話開始、プロフィールボタン */}
                                <div
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '15px',
                                        padding: '20px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '12px',
                                    }}
                                >
                                    <button
                                        onClick={onGoToPlaza}
                                        style={{
                                            background: '#00d4aa',
                                            color: '#333',
                                            border: 'none',
                                            borderRadius: '10px',
                                            cursor: 'pointer',
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            padding: '25px 30px',
                                            width: '80%',
                                        }}
                                    >
                                        📞 通話開始
                                    </button>
                                    <div
                                        style={{
                                            width: '100%',
                                            display: 'flex',
                                            justifyContent: 'space-around',
                                            gap: '10px',
                                        }}
                                    >
                                        <button
                                            onClick={() => {
                                                if (onGoToSettings) {
                                                    onGoToSettings();
                                                } else if (onGoToDetailSettings) {
                                                    onGoToDetailSettings();
                                                } else {
                                                    onGoToProfileSetting();
                                                }
                                            }}
                                            style={{
                                                background: '#5f27cd',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                padding: '12px 20px',
                                                flex: 1,
                                            }}
                                        >
                                            ⚙️ 設定
                                        </button>
                                        <button
                                            onClick={() => {
                                                // PCの場合はメニュー画面内で表示、スマホの場合は別画面へ
                                                if (!isMobile) {
                                                    setShowProfileSettings(true);
                                                } else {
                                                    onGoToProfileSetting();
                                                }
                                            }}
                                            style={{
                                                background: '#5f27cd',
                                                color: '#333',
                                                border: 'none',
                                                borderRadius: '10px',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                fontWeight: 'bold',
                                                padding: '12px 20px',
                                                flex: 1,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '5px',
                                            }}
                                        >
                                            {currentUser.profileImage &&
                                            currentUser.profileImage.trim() !== '' &&
                                            currentUser.profileImage !== 'default_avatar' ? (
                                                <img
                                                    src={currentUser.profileImage}
                                                    alt='プロフィール'
                                                    style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        objectFit: 'cover',
                                                    }}
                                                />
                                            ) : (
                                                <div
                                                    style={{
                                                        width: '20px',
                                                        height: '20px',
                                                        borderRadius: '50%',
                                                        background: 'rgba(255, 255, 255, 0.2)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontSize: '10px',
                                                    }}
                                                >
                                                    {currentUser.username.charAt(0).toUpperCase()}
                                                </div>
                                            )}
                                            <span>プロフィール</span>
                                        </button>
                                    </div>
                                </div>

                                {/* 3. 各種リンク */}
                                <div
                                    style={{
                                        width: '100%',
                                        padding: '30px 20px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: '20px',
                                            fontWeight: 'bold',
                                            marginBottom: '20px',
                                        }}
                                    >
                                        各種リンク
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '40px',
                                            width: '100%',
                                            flexWrap: 'wrap',
                                        }}
                                    >
                                        <a
                                            href='https://twitter.com'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textDecoration: 'none',
                                                color: '#333',
                                                gap: '8px',
                                            }}
                                        >
                                            <span style={{ fontSize: '56px' }}>🐦</span>
                                            <span style={{ fontSize: '16px' }}>
                                                Twitterはこちら
                                            </span>
                                        </a>
                                        <a
                                            href='https://blog.example.com'
                                            target='_blank'
                                            rel='noopener noreferrer'
                                            style={{
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                textDecoration: 'none',
                                                color: '#333',
                                                gap: '8px',
                                            }}
                                        >
                                            <span style={{ fontSize: '56px' }}>📝</span>
                                            <span style={{ fontSize: '16px' }}>ブログはこちら</span>
                                        </a>
                                    </div>
                                </div>

                                {/* 4. ご利用案内 */}
                                <div
                                    style={{
                                        width: '100%',
                                        padding: '30px 20px',
                                        background: 'rgba(255, 255, 255, 0.1)',
                                        backdropFilter: 'blur(10px)',
                                        borderRadius: '12px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: '22px',
                                            fontWeight: 'bold',
                                            marginBottom: '20px',
                                        }}
                                    >
                                        ご利用案内
                                    </div>
                                    <div
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'row',
                                            flexWrap: 'wrap',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: '15px',
                                            width: '100%',
                                        }}
                                    >
                                        {onGoToHelp && (
                                            <button
                                                onClick={onGoToHelp}
                                                style={{
                                                    background: 'transparent',
                                                    color: '#333',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '15px',
                                                    textDecoration: 'underline',
                                                    padding: '5px 10px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                ヘルプ
                                            </button>
                                        )}
                                        {onGoToPrivacySecurity && (
                                            <button
                                                onClick={onGoToPrivacySecurity}
                                                style={{
                                                    background: 'transparent',
                                                    color: '#333',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '15px',
                                                    textDecoration: 'underline',
                                                    padding: '5px 10px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                プライバシーセキュリティ
                                            </button>
                                        )}
                                        {onGoToSecurityPolicy && (
                                            <button
                                                onClick={onGoToSecurityPolicy}
                                                style={{
                                                    background: 'transparent',
                                                    color: '#333',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '15px',
                                                    textDecoration: 'underline',
                                                    padding: '5px 10px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                情報セキュリティ基本方針
                                            </button>
                                        )}
                                        {onGoToCommunityGuidelines && (
                                            <button
                                                onClick={onGoToCommunityGuidelines}
                                                style={{
                                                    background: 'transparent',
                                                    color: '#333',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '15px',
                                                    textDecoration: 'underline',
                                                    padding: '5px 10px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                コミュニティガイドライン
                                            </button>
                                        )}
                                        {onGoToTermsOfService && (
                                            <button
                                                onClick={onGoToTermsOfService}
                                                style={{
                                                    background: 'transparent',
                                                    color: '#333',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '15px',
                                                    textDecoration: 'underline',
                                                    padding: '5px 10px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                利用規約
                                            </button>
                                        )}
                                        {onGoToAbout && (
                                            <button
                                                onClick={onGoToAbout}
                                                style={{
                                                    background: 'transparent',
                                                    color: '#333',
                                                    border: 'none',
                                                    cursor: 'pointer',
                                                    fontSize: '15px',
                                                    textDecoration: 'underline',
                                                    padding: '5px 10px',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                運営者について
                                            </button>
                                        )}
                                    </div>
                                </div>

                                {/* 5. Voitter © 2025 */}
                                <div
                                    style={{
                                        width: '100%',
                                        padding: '20px 0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '14px',
                                        color: '#666',
                                    }}
                                >
                                    Voitter © 2025
                                </div>
                            </div>
                        )}

                        {/* 運営ブログ */}
                        <div
                            style={{
                                width: isMobile ? 'calc(100% - 30px)' : '30%',
                                marginLeft: isMobile ? '15px' : '0',
                                marginRight: isMobile ? '15px' : '0',
                                height: isMobile ? '250px' : '100%',
                                background: 'rgba(255, 255, 255, 0.9)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                padding: '0',
                                boxSizing: 'border-box',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: isMobile ? '16px' : '18px',
                                    fontWeight: 'bold',
                                    borderBottom: '2px solid rgba(0, 0, 0, 0.2)',
                                    padding: '20px 20px 10px 20px',
                                    marginBottom: '0',
                                    position: 'sticky',
                                    top: 0,
                                    background: 'rgba(255, 255, 255, 0.1)',
                                    backdropFilter: 'blur(10px)',
                                    zIndex: 1,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px',
                                }}
                            >
                                <a
                                    href='https://voicechatinfo.kasaneko.com/#blog'
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    style={{
                                        textDecoration: 'none',
                                        color: '#333',
                                        fontSize: isMobile ? '20px' : '22px',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                    }}
                                    title='ブログページを開く'
                                >
                                    🔗
                                </a>
                                📝 運営ブログ
                            </div>
                            <div
                                style={{
                                    width: '100%',
                                    flex: 1,
                                    overflow: 'auto',
                                    padding: '15px 20px 20px 20px',
                                }}
                            >
                                {loading ? (
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            color: '#666',
                                            fontSize: isMobile ? '12px' : '14px',
                                        }}
                                    >
                                        読み込み中...
                                    </div>
                                ) : blogPosts.length === 0 ? (
                                    <div
                                        style={{
                                            textAlign: 'center',
                                            color: '#666',
                                            fontSize: isMobile ? '12px' : '14px',
                                        }}
                                    >
                                        ブログ記事はありません
                                    </div>
                                ) : (
                                    blogPosts.map((item, index) => (
                                        <div
                                            key={item.postId || index}
                                            style={{
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                border: '1px solid rgba(0, 0, 0, 0.15)',
                                                borderRadius: '12px',
                                                marginBottom: '10px',
                                                fontSize: isMobile ? '11px' : '13px',
                                            }}
                                        >
                                            <div
                                                style={{
                                                    fontSize: isMobile ? '10px' : '12px',
                                                    color: '#666',
                                                }}
                                            >
                                                {item.createdAt
                                                    ? new Date(item.createdAt).toLocaleDateString(
                                                          'ja-JP',
                                                          {
                                                              year: 'numeric',
                                                              month: 'long',
                                                              day: 'numeric',
                                                          }
                                                      )
                                                    : ''}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: isMobile ? '12px' : '14px',
                                                    fontWeight: 'bold',
                                                }}
                                            >
                                                {item.title}
                                            </div>
                                            <div
                                                style={{
                                                    fontSize: isMobile ? '11px' : '13px',
                                                    lineHeight: '1.6',
                                                    color: '#333',
                                                    whiteSpace: 'pre-wrap',
                                                }}
                                            >
                                                {item.content}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    {/* スマホ: 各種リンク、ご利用案内、コピーライト */}
                    {isMobile && (
                        <>
                            {/* 各種リンク */}
                            <div
                                style={{
                                    width: '100%',
                                    padding: '30px 20px',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: '18px',
                                        fontWeight: 'bold',
                                        marginBottom: '15px',
                                    }}
                                >
                                    各種リンク
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'row',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '20px',
                                        width: '100%',
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <a
                                        href='https://twitter.com'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textDecoration: 'none',
                                            color: '#333',
                                            gap: '8px',
                                        }}
                                    >
                                        <span style={{ fontSize: '48px' }}>🐦</span>
                                        <span style={{ fontSize: '14px' }}>Twitterはこちら</span>
                                    </a>
                                    <a
                                        href='https://blog.example.com'
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        style={{
                                            display: 'flex',
                                            flexDirection: 'column',
                                            alignItems: 'center',
                                            textDecoration: 'none',
                                            color: '#333',
                                            gap: '8px',
                                        }}
                                    >
                                        <span style={{ fontSize: '48px' }}>📝</span>
                                        <span style={{ fontSize: '14px' }}>ブログはこちら</span>
                                    </a>
                                </div>
                            </div>

                            {/* ご利用案内 */}
                            <div
                                style={{
                                    width: '100%',
                                    padding: '30px 20px',
                                    marginBottom: '20px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box',
                                }}
                            >
                                <div
                                    style={{
                                        fontSize: '20px',
                                        fontWeight: 'bold',
                                        marginBottom: '20px',
                                    }}
                                >
                                    ご利用案内
                                </div>
                                <div
                                    style={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        flexWrap: 'nowrap',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '10px',
                                        width: '100%',
                                    }}
                                >
                                    {onGoToHelp && (
                                        <button
                                            onClick={onGoToHelp}
                                            style={{
                                                background: 'transparent',
                                                color: '#333',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                textDecoration: 'underline',
                                                padding: '5px 8px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            ヘルプ
                                        </button>
                                    )}
                                    {onGoToPrivacySecurity && (
                                        <button
                                            onClick={onGoToPrivacySecurity}
                                            style={{
                                                background: 'transparent',
                                                color: '#333',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                textDecoration: 'underline',
                                                padding: '5px 8px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            プライバシーセキュリティ
                                        </button>
                                    )}
                                    {onGoToSecurityPolicy && (
                                        <button
                                            onClick={onGoToSecurityPolicy}
                                            style={{
                                                background: 'transparent',
                                                color: '#333',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                textDecoration: 'underline',
                                                padding: '5px 8px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            情報セキュリティ基本方針
                                        </button>
                                    )}
                                    {onGoToCommunityGuidelines && (
                                        <button
                                            onClick={onGoToCommunityGuidelines}
                                            style={{
                                                background: 'transparent',
                                                color: '#333',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                textDecoration: 'underline',
                                                padding: '5px 8px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            コミュニティガイドライン
                                        </button>
                                    )}
                                    {onGoToTermsOfService && (
                                        <button
                                            onClick={onGoToTermsOfService}
                                            style={{
                                                background: 'transparent',
                                                color: '#333',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                textDecoration: 'underline',
                                                padding: '5px 8px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            利用規約
                                        </button>
                                    )}
                                    {onGoToAbout && (
                                        <button
                                            onClick={onGoToAbout}
                                            style={{
                                                background: 'transparent',
                                                color: '#333',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                textDecoration: 'underline',
                                                padding: '5px 8px',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            運営者について
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Voitter © 2025 */}
                            <div
                                style={{
                                    width: '100%',
                                    padding: '30px 0',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '13px',
                                    color: '#666',
                                }}
                            >
                                Voitter © 2025
                            </div>
                        </>
                    )}
                </div>
            </div>

            {/* フッター（絶対固定・スマホのみ） */}
            {isMobile && (
                <div
                    style={{
                        position: 'fixed',
                        bottom: 0,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: '90%',
                        maxWidth: '90%',
                        height: '12%',
                        background: 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '15px',
                        paddingBottom: 'max(env(safe-area-inset-bottom, 0px), 20px)',
                        zIndex: 1000,
                    }}
                >
                    {/* 設定ボタン（歯車のみ、通話開始ボタンの左） */}
                    <div style={{ position: 'relative' }}>
                        <button
                            onClick={() => {
                                if (onGoToSettings) {
                                    onGoToSettings();
                                }
                            }}
                            style={{
                                background: 'rgba(255, 255, 255, 0.9)',
                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                borderRadius: '50%',
                                cursor: 'pointer',
                                width: '50px',
                                height: '50px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '24px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            }}
                        >
                            ⚙️
                        </button>
                    </div>

                    {/* 通話開始ボタン */}
                    <button
                        onClick={onGoToPlaza}
                        style={{
                            background: '#00d4aa',
                            color: 'white',
                            border: '6px solid #ff4757',
                            borderRadius: '12px',
                            cursor: 'pointer',
                            fontSize: '20px',
                            fontWeight: 'bold',
                            width: '170px',
                            height: '85px',
                            boxShadow:
                                '0 0 20px rgba(255, 71, 87, 0.6), 0 4px 15px rgba(0, 212, 170, 0.4)',
                            position: 'relative',
                        }}
                    >
                        📞 通話開始
                    </button>

                    {/* プロフィール設定ボタン */}
                    <button
                        onClick={onGoToProfileSetting}
                        style={{
                            background: 'rgba(255, 255, 255, 0.9)',
                            border: '1px solid rgba(0, 0, 0, 0.1)',
                            borderRadius: '50%',
                            cursor: 'pointer',
                            width: '50px',
                            height: '50px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                            padding: 0,
                            overflow: 'hidden',
                        }}
                    >
                        {currentUser.profileImage &&
                        currentUser.profileImage.trim() !== '' &&
                        currentUser.profileImage !== 'default_avatar' ? (
                            <img
                                src={currentUser.profileImage}
                                alt='プロフィール'
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                }}
                            />
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    fontSize: '20px',
                                    color: 'white',
                                    fontWeight: 'bold',
                                }}
                            >
                                {currentUser.username.charAt(0).toUpperCase()}
                            </div>
                        )}
                    </button>
                </div>
            )}

            {/* PC用：プロフィール設定画面オーバーレイ */}
            {!isMobile && showProfileSettings && (
                <div
                    style={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'rgba(0, 0, 0, 0.5)',
                        zIndex: 2000,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: '20px',
                    }}
                    onClick={() => setShowProfileSettings(false)}
                >
                    <div
                        style={{
                            width: '80%',
                            maxWidth: '900px',
                            maxHeight: '90vh',
                            background:
                                'linear-gradient(135deg, #f5f5f5 0%, #e8e8e8 50%, #dcdcdc 100%)',
                            borderRadius: '15px',
                            overflow: 'hidden',
                            boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
                            display: 'flex',
                            flexDirection: 'column',
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div
                            style={{
                                padding: '20px',
                                borderBottom: '1px solid rgba(0, 0, 0, 0.1)',
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                background: 'rgba(255, 255, 255, 0.9)',
                            }}
                        >
                            <h2 style={{ margin: 0, fontSize: '20px', color: '#333' }}>
                                プロフィール設定
                            </h2>
                            <button
                                onClick={() => setShowProfileSettings(false)}
                                style={{
                                    background: '#667eea',
                                    color: 'white',
                                    border: 'none',
                                    padding: '8px 16px',
                                    borderRadius: '5px',
                                    cursor: 'pointer',
                                    fontSize: '12px',
                                    fontWeight: 'bold',
                                }}
                            >
                                閉じる
                            </button>
                        </div>
                        <div
                            style={{
                                flex: 1,
                                overflowY: 'auto',
                                padding: '20px',
                                display: 'flex',
                            }}
                        >
                            <ProfileSettingScreen
                                ref={profileSettingRef}
                                currentUser={currentUser}
                                onBack={() => setShowProfileSettings(false)}
                                onUpdate={(updatedUser) => {
                                    if (onUpdateUser) {
                                        onUpdateUser(updatedUser);
                                    }
                                    setShowProfileSettings(false);
                                }}
                                onNavigateToDetailSettings={onGoToDetailSettings}
                                showSaveButton={false}
                            />
                        </div>
                        <div
                            style={{
                                padding: '20px',
                                borderTop: '1px solid rgba(0, 0, 0, 0.1)',
                                background: 'rgba(255, 255, 255, 0.9)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                            }}
                        >
                            <button
                                onClick={async () => {
                                    if (profileSettingRef.current) {
                                        await profileSettingRef.current.handleSave();
                                        if (onUpdateUser) {
                                            const userStr = localStorage.getItem('user');
                                            if (userStr) {
                                                const user = JSON.parse(userStr);
                                                onUpdateUser(user);
                                            }
                                        }
                                        setShowProfileSettings(false);
                                    }
                                }}
                                style={{
                                    width: '60%',
                                    padding: '12px',
                                    background: 'linear-gradient(135deg, #4CAF50 0%, #45a049 100%)',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: 'bold',
                                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                                }}
                            >
                                💾 保存する
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MenuScreen;
